/* v2.3 Studio "Data" pane, Power BI style: the report's own tables as a
 * fields tree (only these -- never every table on the platform), and an
 * "Add data" picker that adds a whole data package -- every table of a file,
 * or of a SharePoint site like "MOS Finance" -- in one click. Links between
 * the new tables are found from real values (backend/services/model_links.py)
 * and the confident ones are added to definition.model straight away; the
 * rest show up as suggestions in the Links section below the tree. */
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { BarChart3, ChevronDown, ChevronRight, Database, FileSpreadsheet, Folder, GripVertical, Hash, Link2, Plus, RefreshCw, Sigma, Table2, Type as TypeIcon, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { apiRequest } from "../api/client";
import { dataHubApi } from "../api/dataHub";
import { Badge, Button, EmptyState, Modal, SearchInput, useToast } from "../ds";
import type { FieldDrag } from "./autoMeasure";
import { useDatasets } from "./LeftPanels";
import { ModelPanel, tablesInReport, type ModelLink, type ReportModel } from "./ModelPanel";
import { uid, type SDef } from "./model";
import { MIME_FIELD } from "./StudioCanvas";
import { describeMeasureContext, type MeasureContextLike } from "./measureContext";

interface FieldRow { id: string; source_name: string; display_name: string; data_type: string; field_role: string; is_visible?: boolean; default_aggregation?: string | null }
interface MeasureRow extends MeasureContextLike { id: string; name: string; display_name?: string | null; aggregation?: string | null; source_field?: string | null }
export interface DatasetItem { id: string; name: string; is_active: boolean; group_name?: string | null; source_name?: string | null; data_source_id?: string; description?: string | null }

/** A data package: every table of one file / list, grouped by site. */
export interface Package { key: string; group: string; label: string; tables: DatasetItem[] }

export function packagesOf(list: DatasetItem[]): Array<{ group: string; packages: Package[] }> {
  const groups = new Map<string, Map<string, Package>>();
  for (const d of list) {
    if (!d.is_active) continue;
    const g = d.group_name || "Other";
    const key = d.data_source_id || d.source_name || d.id;
    const inGroup = groups.get(g) ?? new Map<string, Package>();
    const pkg = inGroup.get(key) ?? { key, group: g, label: d.source_name || d.name, tables: [] };
    pkg.tables.push(d);
    inGroup.set(key, pkg);
    groups.set(g, inGroup);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([group, pkgs]) => ({
      group,
      packages: [...pkgs.values()].map((p) => ({ ...p, tables: p.tables.sort((a, b) => a.name.localeCompare(b.name)) })).sort((a, b) => a.label.localeCompare(b.label)),
    }));
}

const AUTO_LINK_CONFIDENCE = 0.75;

/** Adds tables to the report model, then links them (confident links only). */
export function useAddTables(getDef: () => SDef | null, onChange: (next: SDef, key?: string) => void) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const add = async (ids: string[], label: string) => {
    const def = getDef();
    if (!def || !ids.length) return;
    const model: ReportModel = { tables: [], relationships: [], ...((def.model as ReportModel | undefined) ?? {}) };
    const before = new Set(tablesInReport(def));
    const fresh = ids.filter((id) => !before.has(id));
    const tables = [...new Set([...model.tables, ...tablesInReport(def), ...ids])];
    onChange({ ...def, model: { ...model, tables } });
    if (tables.length < 2) {
      toast.success(`Added ${label}`, fresh.length ? `${fresh.length} table${fresh.length === 1 ? "" : "s"} — drag a field onto the canvas to start.` : "Already in this report.");
      return;
    }
    setBusy(true);
    try {
      const r = await dataHubApi.suggestLinks(tables, model.relationships.map((x) => [x.fromDatasetId, x.toDatasetId] as [string, string]));
      const auto = r.links.filter((l) => l.confidence >= AUTO_LINK_CONFIDENCE);
      const latest = getDef() ?? def;
      const m: ReportModel = { tables: [], relationships: [], ...((latest.model as ReportModel | undefined) ?? {}) };
      const have = new Set(m.relationships.map((x) => [x.fromDatasetId, x.toDatasetId].sort().join("|")));
      const newLinks: ModelLink[] = auto
        .filter((l) => !have.has([l.fromDatasetId, l.toDatasetId].sort().join("|")))
        .map((l) => ({ id: uid("link"), fromDatasetId: l.fromDatasetId, fromField: l.fromField, toDatasetId: l.toDatasetId, toField: l.toField, crossFilter: l.crossFilter, active: true }));
      if (newLinks.length) onChange({ ...latest, model: { tables: [...new Set([...m.tables, ...tables])], relationships: [...m.relationships, ...newLinks] } });
      const more = r.links.length - auto.length;
      toast.success(
        `Added ${label}`,
        [`${fresh.length} table${fresh.length === 1 ? "" : "s"}`, newLinks.length ? `${newLinks.length} link${newLinks.length === 1 ? "" : "s"} made` : null, more > 0 ? `${more} more suggested under Links` : null].filter(Boolean).join(" · "),
      );
    } catch {
      toast.success(`Added ${label}`, `${fresh.length} table${fresh.length === 1 ? "" : "s"} — links can be made under Links.`);
    } finally {
      setBusy(false);
    }
  };
  return { add, busy };
}

export function DataPane({
  mode, def, getDef, onChange, activeDatasetId, onPickDataset, onAddField, onChartFromField, openPickerSignal,
}: {
  mode: "admin" | "owner";
  def: SDef;
  getDef: () => SDef | null;
  onChange: (next: SDef, key?: string) => void;
  activeDatasetId: string | null;
  onPickDataset: (id: string) => void;
  onAddField: ((f: FieldDrag) => void) | null;
  /** One click: add the right chart for this field (a total, a trend, a bar chart...). */
  onChartFromField?: (f: FieldDrag) => void;
  /** Bumped by the Guide / Start-here flow to open the "Add data" picker. */
  openPickerSignal?: number;
}) {
  const datasets = useDatasets(mode);
  const byId = useMemo(() => new Map(((datasets.data ?? []) as DatasetItem[]).map((d) => [d.id, d])), [datasets.data]);
  const tables = tablesInReport(def);
  const [q, setQ] = useState("");
  const [picker, setPicker] = useState(false);
  useEffect(() => { if (openPickerSignal) setPicker(true); }, [openPickerSignal]);
  const linksRef = useRef<HTMLDivElement>(null);
  // Tables start collapsed (field lists get long); a search opens the matching ones.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { add, busy } = useAddTables(getDef, onChange);
  const base = mode === "admin" ? "/api/v1/admin/datasets" : "/api/v1/owner/datasets";
  const fieldQs = useQueries({ queries: tables.map((id) => ({ queryKey: ["studio-fields", mode, id], queryFn: () => apiRequest<FieldRow[]>(`${base}/${id}/fields`), staleTime: 5 * 60_000 })) });
  const measureQs = useQueries({ queries: tables.map((id) => ({ queryKey: ["studio-measures", mode, id], queryFn: () => apiRequest<MeasureRow[]>(`${base}/${id}/measures`), staleTime: 5 * 60_000 })) });

  const usedByVisuals = useMemo(() => {
    const s = new Set<string>();
    for (const p of def.pages ?? []) for (const w of p.widgets ?? []) if (w.datasetId) s.add(w.datasetId);
    for (const g of def.globalFilters ?? []) if (g?.datasetId) s.add(g.datasetId);
    return s;
  }, [def]);
  const model: ReportModel = { tables: [], relationships: [], ...((def.model as ReportModel | undefined) ?? {}) };
  const removeTable = (id: string) => onChange({ ...def, model: { tables: model.tables.filter((t) => t !== id), relationships: model.relationships.filter((r) => r.fromDatasetId !== id && r.toDatasetId !== id) } });

  const needle = q.trim().toLowerCase();
  const drag = (datasetId: string, kind: "dimension" | "measure", name: string, extra?: Partial<FieldDrag>) => (e: React.DragEvent) => {
    e.dataTransfer.setData(MIME_FIELD, JSON.stringify({ kind, name, datasetId, ...extra }));
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div className="st-pane">
      <div className="ds-row" style={{ gap: 6, marginBottom: 8 }}>
        <div className="st-section__label" style={{ margin: 0 }}>Tables in this report</div>
        <div className="ds-grow" />
        <Button size="sm" variant="primary" icon={Plus} loading={busy} onClick={() => setPicker(true)}>Add data</Button>
      </div>
      {tables.length > 1 ? <Button size="sm" variant="ghost" icon={Link2} onClick={() => { linksRef.current?.scrollIntoView({ block: "start" }); linksRef.current?.focus(); }} style={{ marginBottom: 8 }}>Table links ({(def.model?.relationships ?? []).length})</Button> : null}
      {tables.length > 0 ? <div className="st-hint">Click <BarChart3 size={12} style={{ verticalAlign: -2 }} /> next to a column to get a chart, or drag a column onto the page. Numbers become totals, dates become trends, names become bar charts.</div> : null}
      {tables.length === 0 ? (
        <EmptyState icon={Database} title="Add your data" description="Step 1: pick the file or SharePoint site your numbers live in (e.g. all of MOS Finance). Its tables and the links between them are added in one click." action={<Button variant="primary" icon={Plus} onClick={() => setPicker(true)}>Add data</Button>} />
      ) : (
        <>
          <div style={{ marginBottom: 8 }}><SearchInput value={q} onChange={setQ} placeholder="Search fields in these tables" width="100%" /></div>
          {tables.map((id, i) => {
            const d = byId.get(id);
            const fields = ((fieldQs[i]?.data as FieldRow[] | undefined) ?? []).filter((f) => f.is_visible !== false);
            const measures = (measureQs[i]?.data as MeasureRow[] | undefined) ?? [];
            const fs = fields.filter((f) => !needle || f.display_name.toLowerCase().includes(needle));
            const ms = measures.filter((m) => !needle || (m.display_name ?? m.name).toLowerCase().includes(needle));
            if (needle && !fs.length && !ms.length) return null;
            const open = needle ? true : !!expanded[id];
            return (
              <div key={id} className="st-tree">
                <div className={`st-field st-tree__table${id === activeDatasetId ? " is-active" : ""}`} onClick={() => onPickDataset(id)} title={d ? `${d.group_name ?? ""} · ${d.source_name ?? ""}` : undefined}>
                  <button type="button" className="st-mini" aria-label={open ? "Collapse" : "Expand"} onClick={(e) => { e.stopPropagation(); setExpanded((c) => ({ ...c, [id]: !c[id] })); }}>
                    {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </button>
                  <Table2 size={14} color="var(--accent)" />
                  <span className="ds-grow ds-truncate" style={{ fontWeight: 600 }}>{d?.name ?? "Table"}</span>
                  <span className="ds-small ds-faint">{fields.length || ""}</span>
                  {!usedByVisuals.has(id) ? <button type="button" className="st-mini" aria-label={`Remove ${d?.name ?? "table"} from this report`} onClick={(e) => { e.stopPropagation(); removeTable(id); }}><X size={12} /></button> : null}
                </div>
                {open ? (
                  <div className="st-tree__children">
                    {fieldQs[i]?.isLoading ? <div className="ds-small ds-faint" style={{ padding: "2px 8px" }}>Loading…</div> : null}
                    {ms.map((m) => (
                      <div key={m.id} className="st-field" draggable onDragStart={drag(id, "measure", m.name)} title={[m.expression || `${m.aggregation ?? ""} ${m.source_field ?? ""}`.trim(), describeMeasureContext(m)].filter(Boolean).join(" · ")}>
                        <GripVertical size={12} className="ds-faint" />
                        <Sigma size={13} color="var(--success)" />
                        <span className="ds-grow ds-truncate">{m.display_name || m.name}</span>
                        {onChartFromField ? <button type="button" className="st-mini" aria-label={`Add a chart from ${m.display_name || m.name}`} title="Add a chart from this" onClick={() => onChartFromField({ kind: "measure", name: m.name, datasetId: id, label: m.display_name || m.name })}><BarChart3 size={12} /></button> : null}
                        {onAddField ? <button type="button" className="st-mini" aria-label="Add to selected visual" title="Add to the selected visual" onClick={() => onAddField({ kind: "measure", name: m.name, datasetId: id })}><Plus size={12} /></button> : null}
                      </div>
                    ))}
                    {fs.map((f) => {
                      const numeric = f.field_role === "measure";
                      const extra = numeric ? { numeric: true, label: f.display_name, aggregation: f.default_aggregation } : undefined;
                      return (
                        <div key={f.id} className="st-field" draggable onDragStart={drag(id, "dimension", f.source_name, { ...(extra ?? {}), label: f.display_name, dataType: f.data_type })} title={numeric ? "Number — drop it on Values to show its total" : undefined}>
                          <GripVertical size={12} className="ds-faint" />
                          {f.data_type === "number" ? <Hash size={13} className="ds-faint" /> : <TypeIcon size={13} className="ds-faint" />}
                          <span className="ds-grow ds-truncate">{f.display_name}</span>
                          {numeric ? <Badge>num</Badge> : null}
                          {onChartFromField ? <button type="button" className="st-mini" aria-label={`Add a chart from ${f.display_name}`} title="Add a chart from this column" onClick={() => onChartFromField({ kind: "dimension", name: f.source_name, datasetId: id, label: f.display_name, dataType: f.data_type, ...(extra ?? {}) })}><BarChart3 size={12} /></button> : null}
                          {onAddField ? <button type="button" className="st-mini" aria-label="Add to selected visual" title="Add to the selected visual" onClick={() => onAddField({ kind: "dimension", name: f.source_name, datasetId: id, ...(extra ?? {}) })}><Plus size={12} /></button> : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </>
      )}
      {tables.length > 1 ? (
        <div ref={linksRef} tabIndex={-1} aria-label="Table links" style={{ marginTop: 12, borderTop: "1px solid var(--divider)", paddingTop: 10 }}>
          <ModelPanel mode={mode} def={def} onChange={onChange} activeDatasetId={activeDatasetId} onPickDataset={onPickDataset} linksOnly />
        </div>
      ) : null}
      <AddDataModal
        open={picker}
        mode={mode}
        inReport={new Set(tables)}
        onClose={() => setPicker(false)}
        onAdd={(ids, label) => { setPicker(false); void add(ids, label).then(() => ids[0] && onPickDataset(ids[0])); }}
      />
    </div>
  );
}

/** Pick a whole site, a whole file, or single tables. */
export function AddDataModal({ open, mode, inReport, onClose, onAdd }: { open: boolean; mode: "admin" | "owner"; inReport: Set<string>; onClose: () => void; onAdd: (ids: string[], label: string) => void }) {
  const datasets = useDatasets(mode);
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [openPkg, setOpenPkg] = useState<string | null>(null);
  const groups = packagesOf((datasets.data ?? []) as DatasetItem[]);
  const needle = q.trim().toLowerCase();
  const shown = groups
    .map((g) => ({
      ...g,
      packages: g.packages.filter((p) => !needle || `${g.group} ${p.label} ${p.tables.map((t) => t.name).join(" ")}`.toLowerCase().includes(needle)),
    }))
    .filter((g) => g.packages.length);
  const missing = (tables: DatasetItem[]) => tables.filter((t) => !inReport.has(t.id)).map((t) => t.id);
  const lastRefetch = useRef(0);
  return (
    <Modal open={open} onClose={onClose} size="lg" title="Add data" description="Add a whole SharePoint site or file in one click — every table comes with it, and links between them are made for you.">
      <div className="ds-row" style={{ gap: 8, marginBottom: 10 }}>
        <div className="ds-grow"><SearchInput value={q} onChange={setQ} placeholder="Search sites, files or tables — e.g. finance" width="100%" autoFocus /></div>
        <Button variant="ghost" icon={RefreshCw} onClick={() => { if (Date.now() - lastRefetch.current > 3000) { lastRefetch.current = Date.now(); void qc.invalidateQueries({ queryKey: ["studio-datasets", mode] }); } }} title="Reload the list (after adding data in the Data area)">Refresh</Button>
      </div>
      {datasets.isLoading ? <div className="ds-small ds-faint">Loading your data…</div> : null}
      {!datasets.isLoading && shown.length === 0 ? <EmptyState icon={Database} title={needle ? "Nothing matches" : "No data you can use yet"} description={mode === "owner" ? "Ask an admin to share a site's tables with you (Data → a site → Who can build reports)." : "Add a SharePoint file or list in the Data area first."} /> : null}
      <div className="ds-stack" style={{ gap: 10, maxHeight: "60vh", overflow: "auto" }}>
        {shown.map((g) => {
          const all = g.packages.flatMap((p) => p.tables);
          const todo = missing(all);
          return (
            <div key={g.group} style={{ border: "1px solid var(--border)", borderRadius: 10 }}>
              <div className="ds-list__row" style={{ background: "var(--surface-2, var(--surface))", borderTop: 0, borderRadius: "10px 10px 0 0" }}>
                <Folder size={16} color="var(--warning)" />
                <div className="ds-list__main"><div className="ds-list__title">{g.group}</div><div className="ds-list__meta">{g.packages.length} file{g.packages.length === 1 ? "" : "s"} · {all.length} table{all.length === 1 ? "" : "s"}</div></div>
                {todo.length ? <Button size="sm" variant="primary" icon={Plus} onClick={() => onAdd(todo, `all of ${g.group}`)}>Add all {todo.length}</Button> : <Badge tone="success">in this report</Badge>}
              </div>
              {g.packages.map((p) => {
                const pTodo = missing(p.tables);
                const isOpen = openPkg === p.key || !!needle;
                return (
                  <div key={p.key}>
                    <div className="ds-list__row is-clickable" onClick={() => setOpenPkg(isOpen && !needle ? null : p.key)} style={{ paddingLeft: 28 }}>
                      {isOpen ? <ChevronDown size={14} className="ds-faint" /> : <ChevronRight size={14} className="ds-faint" />}
                      <FileSpreadsheet size={15} color="var(--success)" />
                      <div className="ds-list__main"><div className="ds-truncate" style={{ fontWeight: 600, fontSize: 13.5 }}>{p.label}</div><div className="ds-list__meta">{p.tables.length} table{p.tables.length === 1 ? "" : "s"}</div></div>
                      {pTodo.length ? <Button size="sm" icon={Plus} onClick={(e) => { e.stopPropagation(); onAdd(pTodo, p.label); }}>{p.tables.length > 1 ? `Add all ${pTodo.length}` : "Add"}</Button> : <Badge tone="success">added</Badge>}
                    </div>
                    {isOpen ? p.tables.map((t) => (
                      <div key={t.id} className="ds-list__row" style={{ paddingLeft: 64, paddingTop: 6, paddingBottom: 6 }}>
                        <Table2 size={14} className="ds-faint" />
                        <div className="ds-list__main"><div className="ds-truncate" style={{ fontSize: 13 }}>{t.name}</div></div>
                        {inReport.has(t.id) ? <Badge tone="success">added</Badge> : <Button size="sm" variant="ghost" icon={Plus} onClick={() => onAdd([t.id], t.name)}>Add</Button>}
                      </div>
                    )) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="ds-small ds-faint" style={{ marginTop: 10 }}><Link2 size={12} style={{ verticalAlign: -2 }} /> Links: matching columns whose values really overlap (e.g. Account number, Vessel) are linked automatically; weaker matches are suggested under Links.</div>
    </Modal>
  );
}
